import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCaseData } from '../../features/CaseReducer/CaseReducer';
import Loading from '../Loading';
import { Link } from 'react-router-dom';
import { ReadMore } from '../../Buttons/Buttons';

function AllCase() {
  const dispatch = useDispatch();
  const { cases, loading } = useSelector((state) => state.case);
  const { t, i18n } = useTranslation();
  const [perPage, setPerPage] = useState(6);

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight } = document.documentElement;
    const windowHeight = window.innerHeight;

    // when user reaches near bottom, load more
    if (windowHeight + scrollTop + 124 >= scrollHeight && cases.length >= perPage) {
      setPerPage((prev) => prev + 6);
    }
  }, [cases.length, perPage]);

  useEffect(() => {
    dispatch(fetchCaseData({ perPage }));
  }, [dispatch, perPage, i18n.language]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, when: 'beforeChildren' },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.36 } },
    hover: { y: -6, scale: 1.02, transition: { type: 'spring', stiffness: 300 } },
  };

  // currency/number formatter, uses i18n language if available
  const fmt = useMemo(() => {
    try {
      return new Intl.NumberFormat(i18n.language || 'en-US', { maximumFractionDigits: 0 });
    } catch {
      return { format: (v) => String(v) };
    }
  }, [i18n.language]);

  // helper to determine color class based on percent
  const progressColorClass = (pct) => {
    if (pct >= 100) return 'from-green-600 to-green-500';
    if (pct >= 75) return 'from-emerald-500 to-lime-400';
    if (pct >= 50) return 'from-yellow-400 to-yellow-300';
    if (pct >= 25) return 'from-orange-400 to-orange-300';
    return 'from-red-500 to-rose-400';
  };

  return (
    <div className="mx-auto my-3 mt-10 w-10/12 xl:w-11/12">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 gap-8 py-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
      >
        <AnimatePresence>
          {cases.map((caseItem) => {
            // support multiple possible field names for robustness
            const total = Number(
              caseItem.amount ?? caseItem.total_amount ?? caseItem.totalAmount ?? 0
            );
            const paid = Number(
              caseItem.paid_amount ?? caseItem.paidAmount ?? caseItem.paid ?? 0
            );

            // guard division by zero and negative values
            const safeTotal = Math.max(0, total);
            const safePaid = Math.max(0, paid);

            const rawPct = safeTotal > 0 ? (safePaid / safeTotal) * 100 : 0;
            const progress = Math.min(Math.max(Math.round(rawPct), 0), 100); // 0..100 int

            const remaining = Math.max(safeTotal - safePaid, 0);

            const barColor = progressColorClass(progress);

            return (
              <Link to={`/case/${caseItem.id}`} key={caseItem.id}>
                <motion.div
                  variants={cardVariants}
                  whileHover="hover"
                  className="bg-white rounded-md max-w-xl shadow-sm overflow-hidden"
                >
                  {/* Image */}
                  <div className="w-full overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <motion.img
                      src={process.env.REACT_APP_IMG_URL + caseItem.head_image}
                      alt={caseItem.title}
                      className="w-full h-full object-cover"
                      style={{ aspectRatio: '16/9' }}
                    />
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-2">
                    <h3 className="text-xl font-semibold text-gray-800 line-clamp-2">
                      {caseItem?.title
                        ? caseItem.title.length > 28
                          ? caseItem.title.slice(0, 28) + '...'
                          : caseItem.title
                        : ''}
                    </h3>

                    {/* Raised / Goal */}
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <div>
                        <span className="font-medium text-gray-800">
                          {fmt.format(safePaid)}
                        </span>{' '}
                        <span className="text-gray-500">/</span>{' '}
                        <span className="text-gray-600">{fmt.format(safeTotal)}</span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium text-green-600">{progress}%</span>
                      </div>
                    </div>

                    {/* Progress bar (accessible) */}
                    <div
                      className="relative h-3 w-full bg-gray-200 rounded-full overflow-hidden"
                      role="progressbar"
                      aria-valuenow={progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${progress}% funded`}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className={`absolute top-0 left-0 h-full bg-gradient-to-r ${barColor}`}
                        style={{ willChange: 'width' }}
                      />
                    </div>

                    {/* Remaining or completed badge */}
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      {progress >= 100 ? (
                        <span className="inline-block px-2 py-0.5 text-xs font-semibold text-green-800 bg-green-100 rounded">
                          {t ? t('Fully_Funded') : 'Fully Funded'}
                        </span>
                      ) : (
                        <span className="text-xs">
                          {t ? t('Remaining') : 'Remaining'}: <strong>{fmt.format(remaining)}</strong>
                        </span>
                      )}

                      {/* optional extra small details */}
                      <div className="text-xs text-gray-400">
                        {caseItem.donors_count ? `${caseItem.donors_count} ${t ? t('donors') : 'donors'}` : null}
                      </div>
                    </div>
                  </div>

                  <div className="pb-4 px-4">
                    <ReadMore link={`/case/${caseItem.id}`} />
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {loading && <Loading />}
    </div>
  );
}

export default AllCase;
